import { z } from 'zod'
import type { ApiClient } from './client'
import { ackEnvelope, assertAck } from './envelope'

/**
 * Event stores — POST /{clusterId}/api/event_store/{list,predelete,delete,upload}
 *
 * An event store is a SAVED DATASET: a lookup table built from a DQL result
 * or an uploaded csv/xlsx, referenced by workbooks and enrichment buckets.
 * It is NOT an archive of events — there is no stream, window, size or
 * state on a row. Capture A0 (docs/inventory/storage-capture.json,
 * 2026-09-09, gotham) settled the row:
 *
 *     { name, author, created_by, user_id,
 *       data_source: 'DQL' | 'File Upload by <name>',
 *       extension: null | 'csv' | 'xlsx', file_name: null | string,
 *       create_tstamp: epoch seconds (float), last_modified_by,
 *       last_modified_tstamp: epoch seconds (float), scope_id }
 *
 * inside the flat-count envelope `{ TStamp, count, data: [...], message,
 * status }`. The list body is `{ pageno, pagesize, scope_id }`.
 *
 * The writes are CONFIRMED (A1, A2 — same capture, run on a throwaway store):
 *   predelete `{ scope_id, name }` → `data: [...]`, message "workbook and
 *     enrichment bucket list using eventstore" — the impact is the list of
 *     workbooks and enrichment buckets that reference the store. Its item
 *     shape is unobserved (both captured stores had none), so items stay
 *     `unknown` and the caller renders them loosely.
 *   delete `{ predelete_data: <that data>, scope_id, name }` → `data:
 *     'Delete event store Successfully'`; the list drops by one.
 *   upload — multipart `scope_id`, `name`, `file` → `data: [<id>]`, message
 *     "Event store uploaded". Without `name` the backend answers 400
 *     "Invalid store name"; names are letters, digits and underscore.
 */

const nullableString = z.string().nullable().optional()
const nullableTime = z.union([z.string(), z.number()]).nullable().optional()

/** A raw list row — the captured keys; passthrough keeps anything new. */
export const eventStoreRow = z
  .object({
    name: z.string(),
    author: nullableString,
    created_by: nullableString,
    user_id: nullableString,
    data_source: nullableString,
    extension: nullableString,
    file_name: nullableString,
    create_tstamp: nullableTime,
    last_modified_by: nullableString,
    last_modified_tstamp: nullableTime,
    scope_id: nullableString,
  })
  .passthrough()

export type EventStoreRow = z.output<typeof eventStoreRow>

/** How the dataset was built: a saved DQL result, or a file upload. */
export type EventStoreSourceKind = 'dql' | 'upload'

/** The console's view of an event store: camelCase, epoch ms, nulls where the row was silent. */
export interface EventStore {
  name: string
  author: string | null
  createdBy: string | null
  userId: string | null
  /** Verbatim: `'DQL'` or `'File Upload by <name>'`. */
  dataSource: string | null
  sourceKind: EventStoreSourceKind
  /** `'csv'` / `'xlsx'` for uploads; null for DQL. */
  extension: string | null
  fileName: string | null
  /** Epoch ms. */
  createdAt: number | null
  modifiedBy: string | null
  /** Epoch ms. */
  modifiedAt: number | null
  scopeId: string | null
}

/**
 * Epoch ms from the timestamp forms this API is known to use: numeric epoch
 * SECONDS as a number or a string (`'1741197529.1'`), epoch ms, or a date
 * string. Null when nothing parses.
 */
export function eventStoreTimeMs(v: string | number | null | undefined): number | null {
  if (v === null || v === undefined || v === '') return null
  const n = typeof v === 'number' ? v : Number(v)
  if (Number.isFinite(n)) {
    if (n <= 0) return null
    // Seconds until ~year 5138; anything larger is already ms.
    return n < 1e11 ? Math.round(n * 1000) : Math.round(n)
  }
  const parsed = Date.parse(String(v))
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * `'File Upload by …'` is an upload; everything else (`'DQL'`, or nothing) is
 * a DQL result. The capture has one DQL row carrying a csv extension
 * (`HostDetails1`) — `data_source` wins, the extension is kept as reported.
 */
export function eventStoreSourceKind(dataSource: string | null | undefined): EventStoreSourceKind {
  return /^\s*file\s+upload/i.test(dataSource ?? '') ? 'upload' : 'dql'
}

const str = (v: string | null | undefined): string | null => (v === undefined || v === null || v === '' ? null : v)

/** A row becomes an `EventStore`. */
export function normaliseEventStore(row: EventStoreRow): EventStore {
  const dataSource = str(row.data_source)
  return {
    name: row.name,
    author: str(row.author),
    createdBy: str(row.created_by),
    userId: str(row.user_id),
    dataSource,
    sourceKind: eventStoreSourceKind(dataSource),
    extension: str(row.extension)?.toLowerCase() ?? null,
    fileName: str(row.file_name),
    createdAt: eventStoreTimeMs(row.create_tstamp),
    modifiedBy: str(row.last_modified_by),
    modifiedAt: eventStoreTimeMs(row.last_modified_tstamp),
    scopeId: str(row.scope_id),
  }
}

/** The list payload: the flat-count envelope, `data` the array itself (A0). */
export const eventStoreListResponse = ackEnvelope.extend({
  count: z.number().optional(),
  data: z.array(eventStoreRow).optional(),
})

/**
 * What `predelete` reports: the workbooks and enrichment buckets that use the
 * store. `items` is the `data` array verbatim (its item shape is unobserved —
 * A1 saw an empty list), `message` the server's line, `raw` the whole
 * response. `items` is what `delete` sends back as `predelete_data`.
 */
export interface PredeleteResult {
  items: unknown[]
  message: string
  raw: unknown
}

export const predeleteResponse = ackEnvelope.extend({
  data: z.unknown().optional(),
})

export function normalisePredelete(res: z.output<typeof predeleteResponse>): PredeleteResult {
  return {
    items: Array.isArray(res.data) ? res.data : [],
    message: res.message ?? '',
    raw: res,
  }
}

export interface EventStoreScope {
  clusterId: string
  scopeId: string
}

/** Store names the backend accepts (A2): letters, digits, underscore. */
export const EVENT_STORE_NAME_RE = /^[A-Za-z0-9_]+$/

export const isValidEventStoreName = (name: string): boolean => EVENT_STORE_NAME_RE.test(name)

/**
 * A default store name from a file name: the basename without its extension,
 * every other character folded to `_`, empty when nothing survives
 * (`'active devices.csv'` → `'active_devices'`).
 */
export function eventStoreNameFrom(fileName: string): string {
  const base = fileName.replace(/^.*[\\/]/, '').replace(/\.[^.]*$/, '')
  return base.replace(/[^A-Za-z0-9_]+/g, '_').replace(/^_+|_+$/g, '')
}

export interface EventStoreApi {
  list: (q: EventStoreScope) => Promise<EventStore[]>
  predelete: (q: EventStoreScope & { name: string }) => Promise<PredeleteResult>
  /** `predeleteData` is `PredeleteResult.items` — the predelete answer, echoed back. */
  remove: (q: EventStoreScope & { name: string; predeleteData: unknown }) => Promise<void>
  /** Multipart `scope_id`, `name`, `file` (A2). `name` must satisfy `EVENT_STORE_NAME_RE`. */
  upload: (q: EventStoreScope & { name: string; file: File }) => Promise<void>
}

/** One page is the whole list: the biggest tenant seen holds 14 stores. */
const LIST_PAGE_SIZE = 500

export function createEventStoreApi(client: ApiClient): EventStoreApi {
  return {
    list: async ({ clusterId, scopeId }) => {
      const res = await client.request('POST', `/${clusterId}/api/event_store/list`, {
        body: { pageno: 1, pagesize: LIST_PAGE_SIZE, scope_id: scopeId },
        schema: eventStoreListResponse,
      })
      assertAck(res, 'Reading the event stores')
      return (res.data ?? []).map(normaliseEventStore)
    },
    predelete: async ({ clusterId, scopeId, name }) => {
      const res = await client.request('POST', `/${clusterId}/api/event_store/predelete`, {
        body: { scope_id: scopeId, name },
        schema: predeleteResponse,
      })
      assertAck(res, 'Checking the delete impact')
      return normalisePredelete(res)
    },
    remove: async ({ clusterId, scopeId, name, predeleteData }) => {
      const res = await client.request('POST', `/${clusterId}/api/event_store/delete`, {
        body: { predelete_data: predeleteData, scope_id: scopeId, name },
        schema: ackEnvelope,
      })
      assertAck(res, 'Deleting the event store')
    },
    upload: async ({ clusterId, scopeId, name, file }) => {
      // FormData is passed through by the client untouched, with the JSON
      // Content-Type dropped so the browser can set the multipart boundary.
      const body = new FormData()
      body.append('scope_id', scopeId)
      body.append('name', name)
      body.append('file', file, file.name)
      const res = await client.request('POST', `/${clusterId}/api/event_store/upload`, {
        body,
        schema: ackEnvelope,
      })
      assertAck(res, 'Uploading the event store')
    },
  }
}
